import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  useGetConfigurationHistory,
  useUpdateConfiguration,
} from "@/hooks/useConfiguration";
import { Loading } from "@/components/Loading";
import { History, RotateCcw, User, Calendar } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface ConfigurationHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface HistoryRecord {
  id: string;
  settingsId: string;
  changedBy: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  municipalityName: string;
  municipalityState: string;
  estimatedPopulation: number;
  contactEmail: string;
  slaCriticalDays: number;
  slaHighDays: number;
  slaMediumDays: number;
  slaLowDays: number;
  deduplicationRadiusMeters: number;
  deduplicationTimeWindowDays: number;
  occurrenceReopeningDeadlineDays: number;
  changedAt: string;
}

export const ConfigurationHistoryDialog = ({
  open,
  onOpenChange,
}: ConfigurationHistoryDialogProps) => {
  const queryClient = useQueryClient();
  const { data: history, isLoading } = useGetConfigurationHistory();
  const updateConfiguration = useUpdateConfiguration();

  const handleRevert = async (record: HistoryRecord) => {
    try {
      await updateConfiguration.mutateAsync({
        municipalityName: record.municipalityName,
        municipalityState: record.municipalityState,
        estimatedPopulation: record.estimatedPopulation,
        contactEmail: record.contactEmail,
        slaCriticalDays: record.slaCriticalDays,
        slaHighDays: record.slaHighDays,
        slaMediumDays: record.slaMediumDays,
        slaLowDays: record.slaLowDays,
        deduplicationRadiusMeters: record.deduplicationRadiusMeters,
        deduplicationTimeWindowDays: record.deduplicationTimeWindowDays,
        occurrenceReopeningDeadlineDays: record.occurrenceReopeningDeadlineDays,
      });

      toast.success("Configuração revertida com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["getConfiguration"] });
      queryClient.invalidateQueries({ queryKey: ["getConfigurationHistory"] });
      onOpenChange(false);
    } catch (error) {
      toast.error("Erro ao reverter configuração");
    }
  };

  const formatSLA = (days: number) => {
    return `${days}d`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <DialogTitle>Histórico de Configurações</DialogTitle>
          </div>
          <DialogDescription>
            Visualize todas as alterações realizadas nas configurações do
            sistema
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[300px]">
            <Loading label="Carregando histórico..." />
          </div>
        ) : !history || history.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[300px] text-muted-foreground">
            <History className="h-12 w-12 mb-4 opacity-20" />
            <p>Nenhum histórico de alterações encontrado</p>
          </div>
        ) : (
          <div className="h-[60vh] overflow-y-auto pr-4">
            <div className="space-y-4 pb-4">
              {history.map((record: HistoryRecord, index: number) => (
                <Card key={record.id} className="relative overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-base flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {record.changedBy.firstName}{" "}
                          {record.changedBy.lastName}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 text-xs">
                          <Calendar className="h-3 w-3" />
                          {format(
                            new Date(record.changedAt),
                            "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                            { locale: ptBR },
                          )}
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRevert(record)}
                        disabled={updateConfiguration.isPending}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Reverter
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    {/* Informações do Município */}
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                        Município
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Nome:</span>{" "}
                          <span className="font-medium">
                            {record.municipalityName}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Estado:</span>{" "}
                          <span className="font-medium">
                            {record.municipalityState}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            População:
                          </span>{" "}
                          <span className="font-medium">
                            {record.estimatedPopulation.toLocaleString("pt-BR")}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Email:</span>{" "}
                          <span className="font-medium">
                            {record.contactEmail}
                          </span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* SLA */}
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                        Níveis de SLA
                      </h4>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">
                            Crítico
                          </span>
                          <Badge
                            variant="destructive"
                            className="w-fit pointer-events-none"
                          >
                            {formatSLA(record.slaCriticalDays)}
                          </Badge>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">
                            Alto
                          </span>
                          <Badge
                            variant="secondary"
                            className="w-fit bg-orange-100 text-orange-800 pointer-events-none"
                          >
                            {formatSLA(record.slaHighDays)}
                          </Badge>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">
                            Médio
                          </span>
                          <Badge
                            variant="secondary"
                            className="w-fit bg-yellow-100 text-yellow-800 pointer-events-none"
                          >
                            {formatSLA(record.slaMediumDays)}
                          </Badge>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">
                            Baixo
                          </span>
                          <Badge
                            variant="secondary"
                            className="w-fit bg-green-100 text-green-800 pointer-events-none"
                          >
                            {formatSLA(record.slaLowDays)}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Configurações Avançadas */}
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                        Configurações Avançadas
                      </h4>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-xs text-muted-foreground block">
                            Raio de Deduplicação
                          </span>
                          <span className="font-medium">
                            {record.deduplicationRadiusMeters}m
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground block">
                            Janela de Tempo
                          </span>
                          <span className="font-medium">
                            {record.deduplicationTimeWindowDays} dias
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground block">
                            Prazo de Reabertura
                          </span>
                          <span className="font-medium">
                            {record.occurrenceReopeningDeadlineDays} dias
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
